// @ts-check
import { POWER, WORLD } from './tuning.js';
import { querySolids } from './geometry.js';
/** @typedef {import('./player.js').Position} Player */
/** @typedef {import('./types.js').Level} Level */
/** The physical body follows the same growth factor as QP's artwork. @param {Player} state @param {boolean} [standing] */
export function playerBody(state, standing = false) {
  return { width: WORLD.playerWidth * state.size, height: WORLD.playerHeight * state.size * (!standing && (state.crouching || state.sliding) ? POWER.crouch : 1) };
}
/** @param {Level} level @param {number} x @param {number} y @param {number} width @param {number} height */
function clear(level, x, y, width, height) {
  return x >= 14 && x + width <= level.width - 14 && !querySolids(level, x, x + width).some((solid) =>
    x + width > solid.x + 0.001 && x < solid.x + solid.width - 0.001 && y < solid.y + solid.height - 0.001 && y + height > solid.y + 0.001);
}
/** @param {Level} level @param {Player} state */
export function canStand(level, state) {
  const body = playerBody(state, true);
  return clear(level, state.x, state.y, body.width, body.height);
}
/** A crouched QP may crawl forward only into a low gap: somewhere standing would
 * not fit but crouching would. Everywhere else, holding Down still holds QP still.
 * @param {Level} level @param {Player} state @param {number} direction
 */
export function canCrawl(level, state, direction) {
  if (!direction) return false;
  const width = WORLD.playerWidth * state.size, standing = WORLD.playerHeight * state.size;
  const x = state.x + Math.sign(direction) * 6;
  return !clear(level, x, state.y, width, standing) && clear(level, x, state.y, width, standing * POWER.crouch);
}
/** Growth waits for clearance, preserving the feet and center instead of embedding QP in geometry.
 * @param {Level} level @param {Player} state @param {number} dt
 */
export function updatePower(level, state, dt) {
  state.invulnerable = Math.max(0, state.invulnerable - dt);
  state.recoil = Math.max(0, state.recoil - dt);
  state.overclock = Math.max(0, state.overclock - dt);
  state.shotCooldown = Math.max(0, state.shotCooldown - dt);
  if (state.power === 'base' || state.size === POWER.size) return;
  const width = WORLD.playerWidth * POWER.size, height = WORLD.playerHeight * POWER.size;
  const x = state.x + (playerBody(state).width - width) / 2;
  if (clear(level, x, state.y, width, height)) { state.x = x; state.size = POWER.size; }
}
/** @param {Level} level @param {Player} state @param {'core'|'pulse'|'overclock'} kind @param {import('./types.js').EventSink} [emit] */
export function collectPower(level, state, kind, emit) {
  if (kind === 'overclock') state.overclock = POWER.overclockTime;
  else if (kind === 'pulse' || state.power === 'base') state.power = kind;
  updatePower(level, state, 0);
  emit?.({ type: 'pickup', x: state.x, y: state.y, reward: kind });
}
/** Contact consumes an upgrade once; brief immunity prevents stacked enemies taking another hit.
 * @param {Level} level @param {Player} state @param {import('./types.js').Enemy} enemy @param {import('./types.js').EventSink} [emit]
 */
export function damagePlayer(level, state, enemy, emit) {
  if (state.invulnerable || state.overclock) return;
  if (state.power === 'base' && !level.assist) {
    level.status = 'dead'; state.vx = 0;
    level.reason = enemy.mode === 'roll' ? 'Hit by a rolling security core' : `Hit by a ${enemy.kind}`;
    return;
  }
  // Assist turns the hit that would disable base QP into a knockback.
  const shielded = state.power === 'base';
  loseUpgrade(state);
  state.recoil = POWER.recoil;
  state.vx = state.x + WORLD.playerWidth / 2 < enemy.x + enemy.width / 2 ? -160 : 160;
  state.vy = 220; state.grounded = false; state.jumps = 1;
  state.crouching = state.sliding = false; state.pound = 'none'; state.impact = 0;
  emit?.({ type: 'damaged', x: state.x, y: state.y, ...(shielded ? { reason: 'assist-shield' } : {}) });
}
/** One step down the upgrade ladder, then brief immunity. @param {Player} state */
export function loseUpgrade(state) {
  const width = playerBody(state).width;
  if (state.power !== 'base') state.power = state.power === 'pulse' ? 'core' : 'base';
  if (state.power === 'base' && state.size !== 1) { state.size = 1; state.x += (width - WORLD.playerWidth) / 2; }
  state.invulnerable = POWER.immunity;
}
